import type { ApiPaginatedResponse, ApiResponse } from '@bcube/shared';
import axios from 'axios';
import { api } from '@/lib/api';
import type {
  Contract,
  ContractListParams,
  ContractSignatureStatus,
  ContractVersion,
  CreateContractPayload,
  PaginatedContracts,
  UpdateContractPayload,
} from '@/types/commercial/contracts';

interface ContractErrorBody {
  message?: string;
  error?: string;
  code?: string;
  field?: string;
}

export class ContractApiError extends Error {
  constructor(
    message: string,
    public readonly status?: number,
    public readonly code?: string,
    public readonly field?: string,
    public readonly diagnostics?: ContractPdfDiagnostics,
  ) {
    super(message);
    this.name = 'ContractApiError';
  }
}

function mapError(error: unknown, fallback: string): ContractApiError {
  if (axios.isAxiosError<ContractErrorBody>(error)) {
    const body = error.response?.data;
    return new ContractApiError(
      body?.message ?? fallback,
      error.response?.status,
      body?.code,
      body?.field,
    );
  }
  return new ContractApiError(error instanceof Error ? error.message : fallback);
}

async function mapPdfError(error: unknown, fallback: string): Promise<ContractApiError> {
  if (axios.isAxiosError<Blob | ContractErrorBody>(error)) {
    let body = error.response?.data;
    const diagnostics: ContractPdfDiagnostics = {
      status: error.response?.status,
      contentType: error.response?.headers['content-type'],
      contentDisposition: error.response?.headers['content-disposition'],
      blobSize: body instanceof Blob ? body.size : undefined,
      blobType: body instanceof Blob ? body.type : undefined,
    };
    if (body instanceof Blob && body.type.includes('json')) {
      try {
        body = JSON.parse(await body.text()) as ContractErrorBody;
      } catch {
        body = undefined;
      }
    }
    const errorBody = body instanceof Blob ? undefined : body;
    return new ContractApiError(
      errorBody?.message ?? fallback,
      error.response?.status,
      errorBody?.code,
      errorBody?.field,
      diagnostics,
    );
  }
  return new ContractApiError(error instanceof Error ? error.message : fallback);
}

async function request<T>(operation: () => Promise<T>, fallback: string): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    throw mapError(error, fallback);
  }
}

function toPaginated<T>(response: ApiPaginatedResponse<T>): PaginatedContracts<T> {
  return {
    docs: response.data,
    total: response.meta.total,
    pages: response.meta.pages,
    page: response.meta.page,
    limit: response.meta.limit,
  };
}

function fileNameFromDisposition(disposition: string | undefined): string | undefined {
  if (!disposition) return undefined;
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) return decodeURIComponent(encoded);
  return disposition.match(/filename="([^"]+)"/i)?.[1];
}

export interface ContractPdfFile {
  blob: Blob;
  fileName: string;
  status: number;
  contentType: string;
  contentDisposition?: string;
}

export interface ContractPdfDiagnostics {
  status?: number;
  contentType?: string;
  contentDisposition?: string;
  blobSize?: number;
  blobType?: string;
}

export const contractApi = {
  findMany: (params: ContractListParams): Promise<PaginatedContracts<Contract>> =>
    request(async () => {
      const { data } = await api.get<ApiPaginatedResponse<Contract>>('/commercial/contracts', {
        params,
      });
      return toPaginated(data);
    }, 'Erro ao carregar contratos.'),

  findOne: (id: string): Promise<Contract> =>
    request(async () => {
      const { data } = await api.get<ApiResponse<Contract>>(`/commercial/contracts/${id}`);
      return data.data;
    }, 'Erro ao carregar contrato.'),

  create: (payload: CreateContractPayload): Promise<Contract> =>
    request(async () => {
      const { data } = await api.post<ApiResponse<Contract>>('/commercial/contracts', payload);
      return data.data;
    }, 'Erro ao criar contrato.'),

  update: (id: string, payload: UpdateContractPayload): Promise<Contract> =>
    request(async () => {
      const { data } = await api.patch<ApiResponse<Contract>>(
        `/commercial/contracts/${id}`,
        payload,
      );
      return data.data;
    }, 'Erro ao atualizar contrato.'),

  activate: (id: string): Promise<Contract> =>
    request(async () => {
      const { data } = await api.post<ApiResponse<Contract>>(
        `/commercial/contracts/${id}/activate`,
      );
      return data.data;
    }, 'Erro ao ativar contrato.'),

  inactivate: (id: string): Promise<Contract> =>
    request(async () => {
      const { data } = await api.post<ApiResponse<Contract>>(
        `/commercial/contracts/${id}/inactivate`,
      );
      return data.data;
    }, 'Erro ao inativar contrato.'),

  createVersion: (id: string): Promise<ContractVersion> =>
    request(async () => {
      const { data } = await api.post<ApiResponse<ContractVersion>>(
        `/commercial/contracts/${id}/versions`,
      );
      return data.data;
    }, 'Erro ao publicar nova versão.'),

  findVersions: (
    id: string,
    params: { page?: number; limit?: number },
  ): Promise<PaginatedContracts<ContractVersion>> =>
    request(async () => {
      const { data } = await api.get<ApiPaginatedResponse<ContractVersion>>(
        `/commercial/contracts/${id}/versions`,
        { params },
      );
      return toPaginated(data);
    }, 'Erro ao carregar histórico do contrato.'),

  findVersion: (id: string, version: number): Promise<ContractVersion> =>
    request(async () => {
      const { data } = await api.get<ApiResponse<ContractVersion>>(
        `/commercial/contracts/${id}/versions/${version}`,
      );
      return data.data;
    }, 'Erro ao consultar versão do contrato.'),

  getPdf: (
    id: string,
    version: number,
    disposition: 'inline' | 'attachment',
  ): Promise<ContractPdfFile> => {
    const fallback =
      disposition === 'inline' ? 'Erro ao visualizar contrato.' : 'Erro ao baixar contrato.';
    return (async () => {
      try {
        const response = await api.get<Blob>(
          `/commercial/contracts/${id}/versions/${version}/pdf`,
          { params: { disposition }, responseType: 'blob' },
        );
        const contentType = response.headers['content-type'] ?? response.data.type;
        const contentDisposition = response.headers['content-disposition'];
        const magic = await response.data.slice(0, 5).text();
        if (!contentType?.includes('application/pdf') || magic !== '%PDF-') {
          throw new ContractApiError('A API retornou um arquivo que não é um PDF válido.');
        }
        return {
          blob: response.data,
          fileName: fileNameFromDisposition(contentDisposition) ?? `contrato-v${version}.pdf`,
          status: response.status,
          contentType,
          contentDisposition,
        };
      } catch (error) {
        if (error instanceof ContractApiError) throw error;
        throw await mapPdfError(error, fallback);
      }
    })();
  },

  findSignatureStatus: (versionId: string): Promise<ContractSignatureStatus> =>
    request(async () => {
      const { data } = await api.get<ApiResponse<ContractSignatureStatus>>(
        `/commercial/contracts/versions/${versionId}/signature`,
      );
      return data.data;
    }, 'Erro ao consultar assinatura do contrato.'),

  sendToSignature: (versionId: string): Promise<ContractSignatureStatus> =>
    request(async () => {
      const { data } = await api.post<ApiResponse<ContractSignatureStatus>>(
        `/commercial/contracts/versions/${versionId}/signature`,
      );
      return data.data;
    }, 'Erro ao enviar contrato para assinatura.'),

  findByProposal: (proposalId: string): Promise<Contract[]> =>
    request(async () => {
      const { data } = await api.get<ApiResponse<Contract[]>>(
        `/commercial/proposals/${proposalId}/contracts`,
      );
      return data.data;
    }, 'Erro ao carregar contratos da proposta.'),

  syncProposal: (proposalId: string): Promise<Contract[]> =>
    request(async () => {
      const { data } = await api.post<ApiResponse<Contract[]>>(
        `/commercial/proposals/${proposalId}/contracts/sync`,
      );
      return data.data;
    }, 'Erro ao identificar contratos da proposta.'),
};
